import { availableAuditEntriesAtom, userDataAtom } from '@/global-store';
import LoadingButton from '@components/Button/LoadingButton';
import {
  FormDomainSelect,
  useDomainOptions,
} from '@components/FormDomainSelect';
import { useRescanAuditEntries } from '@http/queries/useRescanAuditEntries';
import {
  Button,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTitle,
  useNotification,
} from '@eye-able/ui';
import type { AuditData } from '@eye-able/types';
import { IconRadar } from '@tabler/icons-react';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

type RescannableAuditEntry = Pick<
  AuditData,
  'id' | 'domain' | 'maxRescans' | 'rescanCounter'
>;

export function getRemainingRescans(entry: RescannableAuditEntry): number {
  return Math.max(0, (entry.maxRescans ?? 0) - (entry.rescanCounter ?? 0));
}

/** Lets governance editors select one or more report domains for an immediate rescan. */
export function PolicyRescanControl() {
  const { t } = useInlineTranslation();
  const notification = useNotification();
  const auditEntries = useAtomValue(availableAuditEntriesAtom);
  const domainOptions = useDomainOptions();
  const user = useAtomValue(userDataAtom);
  const rescanAuditEntries = useRescanAuditEntries();
  const [isOpen, setIsOpen] = useState(false);
  const [dialogContentElement, setDialogContentElement] =
    useState<HTMLDivElement | null>(null);
  const [selectedAuditEntryIds, setSelectedAuditEntryIds] = useState<number[]>(
    [],
  );

  const isPrivilegedUser =
    user?.isAdmin === true || user?.isSalesAdmin === true;
  const remainingRescansByAuditEntryId = useMemo(
    () =>
      new Map(
        auditEntries.map((entry) => [entry.id, getRemainingRescans(entry)]),
      ),
    [auditEntries],
  );
  const availableDomainOptions = useMemo(
    () =>
      domainOptions.filter(
        (option) =>
          isPrivilegedUser ||
          (remainingRescansByAuditEntryId.get(option.id) ?? 0) > 0,
      ),
    [domainOptions, isPrivilegedUser, remainingRescansByAuditEntryId],
  );
  const availableDomainIds = useMemo(
    () => new Set(availableDomainOptions.map((option) => option.id)),
    [availableDomainOptions],
  );

  useEffect(() => {
    setSelectedAuditEntryIds((selectedIds) => {
      const availableSelectedIds = selectedIds.filter((id) =>
        availableDomainIds.has(id),
      );

      return availableSelectedIds.length === selectedIds.length
        ? selectedIds
        : availableSelectedIds;
    });
  }, [availableDomainIds]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSelectedAuditEntryIds([]);
  };

  const handleRescan = async () => {
    await rescanAuditEntries.mutateAsync({
      auditEntryIds: selectedAuditEntryIds,
    });
    notification.success(t('checkScheduleSuccess'));
    handleOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Button
        variant='yellow'
        disabled={availableDomainOptions.length === 0}
        onClick={() => setIsOpen(true)}
      >
        <IconRadar aria-hidden />
        {t('checkAgainButton')}
      </Button>
      <DialogContent
        className='eye:w-full'
        ref={setDialogContentElement}
        maxWidth='md'
      >
        <DialogTitle>{t('selectDomains')}</DialogTitle>
        <FormDomainSelect
          label={t('includedDomains')}
          options={availableDomainOptions}
          value={selectedAuditEntryIds}
          placeholder={t('selectDomains')}
          onValueChange={setSelectedAuditEntryIds}
          slotProps={{
            content: {
              container: dialogContentElement ?? undefined,
            },
          }}
        />
        <DialogActions>
          <DialogClose asChild>
            <Button type='button' variant='outline'>
              {t('cancel')}
            </Button>
          </DialogClose>
          <LoadingButton
            type='button'
            variant='yellow'
            loading={rescanAuditEntries.isPending}
            disabled={selectedAuditEntryIds.length === 0}
            onClick={() => void handleRescan()}
          >
            <IconRadar aria-hidden />
            {t('checkAgainButton')}
          </LoadingButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}
